import { motion, useInView, useReducedMotion } from "motion/react";
import { type ReactNode, useRef } from "react";

/**
 * Entrance motion, on springs rather than on a duration.
 *
 * The difference is not decorative. A `cubic-bezier` over 620ms is a curve
 * someone drew; a spring is solved from stiffness, damping and mass, so the
 * element decelerates the way something with weight decelerates and the
 * overshoot is a consequence rather than a keyframe. Side by side, the eased
 * version reads as a slide and the spring reads as an arrival.
 *
 * Same discipline as before, and it still matters more than the easing:
 *
 *   **Once.** `useInView(..., { once: true })`. An element that re-animates
 *   every time it re-enters the viewport turns scrolling back up into a
 *   slideshow.
 *
 *   **Short travel.** 18px and a 2% scale. Long travel is what makes a page
 *   look like it is assembling itself on arrival.
 *
 *   **Stagger capped.** Six steps at 60ms. Ten projects at full stagger means
 *   the last one lands after the visitor has finished reading the first.
 *
 * `data-reveal` / `data-shown` are kept on the DOM even though Motion no
 * longer needs them: `npm run shots` and `npm run a11y` both assert that
 * nothing is left unrevealed, and those checks are the reason an animation
 * whose resting state is `opacity: 0` is safe to ship at all.
 */

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Milliseconds. Use `stagger(index)` for lists rather than picking numbers. */
  delay?: number;
}

const HIDDEN = { opacity: 0, y: 18, scale: 0.985 };
const SHOWN = { opacity: 1, y: 0, scale: 1 };

/** Underdamped just enough to settle rather than bounce. */
const SPRING = {
  type: "spring",
  stiffness: 140,
  damping: 22,
  mass: 0.9,
} as const;

function useRevealState() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -8% 0px" });
  const reduced = useReducedMotion() ?? false;
  return { ref, reduced, shown: reduced || inView };
}

function transition(reduced: boolean, delay: number) {
  return reduced ? { duration: 0 } : { ...SPRING, delay: delay / 1000 };
}

export function Reveal({ children, className, delay = 0 }: RevealProps) {
  const { ref, reduced, shown } = useRevealState();
  return (
    <motion.div
      ref={ref as React.RefObject<HTMLDivElement>}
      data-reveal
      data-shown={shown ? "" : undefined}
      initial={reduced ? false : HIDDEN}
      animate={shown ? SHOWN : HIDDEN}
      transition={transition(reduced, delay)}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * The list-item variant.
 *
 * Two components rather than a polymorphic `as` prop, because `<div>` inside
 * `<ul>` is invalid HTML that breaks the list semantics a screen reader uses
 * to announce "list, 6 items". A generic that can render the wrong tag is
 * worse than two named components that cannot.
 */
export function RevealItem({ children, className, delay = 0 }: RevealProps) {
  const { ref, reduced, shown } = useRevealState();
  return (
    <motion.li
      ref={ref as React.RefObject<HTMLLIElement>}
      data-reveal
      data-shown={shown ? "" : undefined}
      initial={reduced ? false : HIDDEN}
      animate={shown ? SHOWN : HIDDEN}
      transition={transition(reduced, delay)}
      className={className}
    >
      {children}
    </motion.li>
  );
}

const STAGGER_STEP_MS = 60;
const MAX_STAGGER_STEPS = 5;

export function stagger(index: number): number {
  return Math.min(index, MAX_STAGGER_STEPS) * STAGGER_STEP_MS;
}
